import React, { useEffect, useState } from 'react';

import { Box, Button, Dialog, Grid, IconButton, InputAdornment } from '@material-ui/core';
import { DateRange as DateRangeIcon } from '@material-ui/icons';
import { format, isValid, parse } from 'date-fns';
import { pt } from 'date-fns/locale';
import { NumberFormatValues } from 'react-number-format';

import 'styled-components/macro';
import { DateRangePicker } from './dateRangePicker';
import { DateRangeType } from './types';

import { MaskedInput } from 'components';
import { LabelSelectCN } from 'components/SelectCN/LabelSelectCN';

type Props = {
  onChange?: (arg: DateRangeType | undefined) => void;
  error?: string;
  grid?: {
    left?: React.ComponentProps<typeof Grid>;
    right?: React.ComponentProps<typeof Grid>;
  };
  value: string;
  setValue: (value: string) => void;
};

export function DateRangeSelect({ onChange, error, grid, value, setValue }: Props) {
  const [isDialogOpen, dispatch] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeType>();
  const [initialDate, setInitialDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    onChange && onChange(dateRange);
  }, [dateRange]);

  const dateMask = '##/##/####';
  const dateFormat = 'dd/MM/yyyy';

  function handleValue(value: any) {
    const index = value.target.value;
    if (index === 0) {
      setValue('dateRangePayment');
    } else if (index === 1) {
      setValue('dateRangeDue');
    } else if (index === 2) {
      setValue('dateRangeRegistry');
    }
  }

  return (
    <Grid
      item
      xs={8}
      style={{
        paddingRight: 0,
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{
          gap: '0.25rem',
        }}
      >
        <LabelSelectCN
          value={value}
          options={['Data de Pagamento', 'Data de Vencimento', 'Data de Cadastro']}
          getValue={handleValue}
          text="uppercase"
        />
        <Grid container direction="row" spacing={2} xs={12}>
          <Grid item xs={12} md={6} {...grid?.left}>
            <MaskedInput
              fullWidth
              endAdornment={
                <InputAdornment style={{ paddingRight: 0 }} position="end">
                  <IconButton
                    aria-label="open date dialog"
                    onClick={() => dispatch(true)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    <DateRangeIcon
                      style={{
                        color: '#A0A0A0',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              }
              errorMessage={
                (initialDate.replace('_', '').length === dateMask.length &&
                  isValid(dateRange?.startDate) === false &&
                  'Data Invalida') ||
                error
              }
              format={dateMask}
              mask="_"
              value={initialDate}
              onValueChange={(values: NumberFormatValues) => {
                setInitialDate(values.formattedValue);
                setDateRange(oldRange => ({
                  ...oldRange,
                  startDate: parse(values.formattedValue ?? '', dateFormat, new Date()),
                }));
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            style={{
              paddingRight: 0,
            }}
            {...grid?.right}
          >
            <MaskedInput
              fullWidth
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="open date dialog"
                    onClick={() => dispatch(true)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    <DateRangeIcon
                      style={{
                        color: '#A0A0A0',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              }
              errorMessage={
                endDate.replace('_', '').length === dateMask.length &&
                isValid(dateRange?.endDate) === false &&
                'Data Invalida'
              }
              format={dateMask}
              mask="_"
              value={endDate}
              onValueChange={(values: NumberFormatValues) => {
                setEndDate(values.formattedValue);
                setDateRange(oldRange => ({
                  ...oldRange,
                  endDate: parse(values?.formattedValue ?? '', dateFormat, new Date()),
                }));
              }}
            />
          </Grid>
          <Dialog
            open={isDialogOpen}
            onClose={() => dispatch(false)}
            aria-labelledby="date-dialog-title"
            css={`
              .MuiDialog-paperWidthSm {
                max-width: none;
              }
            `}
          >
            <DateRangePicker
              initialDateRange={{
                startDate: isValid(dateRange?.startDate) ? dateRange?.startDate : undefined,
                endDate: isValid(dateRange?.endDate) ? dateRange?.endDate : undefined,
              }}
              onChange={range => {
                setDateRange(range);
                range.startDate && setInitialDate(format(range.startDate, dateFormat));
                range.endDate && setEndDate(format(range.endDate, dateFormat));
              }}
              translation={{
                endDate: 'Data Final',
                startDate: 'Data Inicial',
                months: ['JAN', 'FER', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'],
                weekDays: ['DOM', 'SEG', 'TER', 'QUAR', 'QUIN', 'SEX', 'SAB'],
                locale: pt,
              }}
              slot={
                <Box display="flex" justifyContent="flex-end">
                  <Button onClick={() => dispatch(false)} color="primary">
                    OK
                  </Button>
                </Box>
              }
            />
          </Dialog>
        </Grid>
      </Box>
    </Grid>
  );
}
